import { type CmdKey, commandsCtx } from '@milkdown/kit/core';
import type { Ctx } from '@milkdown/kit/ctx';
import type { Node as ProseNode } from '@milkdown/kit/prose/model';
import { TextSelection } from '@milkdown/kit/prose/state';
import { $command, $useKeymap } from '@milkdown/kit/utils';

/**
 * Command to insert a new grouped-cards node with placeholder blockquotes.
 *
 * Creates a grouped-cards container with two default blockquotes:
 * - Win card (✅)
 * - Warning card (⚠️)
 */
export const insertGroupedCardsCommand = $command('InsertGroupedCards', () => {
  return () => (state, dispatch) => {
    const { schema, tr } = state;
    const { grouped_cards, blockquote, paragraph } = schema.nodes;

    // Create placeholder blockquotes
    const winCard = blockquote.create({}, paragraph.create({}, schema.text('✅ **Win:** ')));

    const warningCard = blockquote.create(
      {},
      paragraph.create({}, schema.text('⚠️ **Warning:** '))
    );

    // Create grouped-cards node containing the blockquotes
    const groupedCardsNode = grouped_cards.create({}, [winCard, warningCard]);

    // Insert at current position
    const { from } = state.selection;

    if (dispatch) {
      tr.insert(from, groupedCardsNode);

      // Move cursor inside the first blockquote
      const resolvedPos = tr.doc.resolve(from + 2); // +2 to enter first blockquote
      tr.setSelection(TextSelection.near(resolvedPos));

      dispatch(tr);
    }

    return true;
  };
});

/**
 * Command to ungroup cards - converts grouped_cards node to separate blockquotes.
 *
 * Finds the grouped_cards ancestor of the current selection and replaces it
 * with its child blockquotes as separate top-level blocks.
 */
export const ungroupCardsCommand = $command('UngroupCards', () => {
  return () => (state, dispatch) => {
    const { selection, tr } = state;
    const { $from } = selection;

    // Find the grouped_cards node ancestor
    let groupedCardsNode = null;
    let groupedCardsPos = null;

    for (let depth = $from.depth; depth > 0; depth--) {
      const node = $from.node(depth);
      if (node.type.name === 'grouped_cards') {
        groupedCardsNode = node;
        groupedCardsPos = $from.before(depth);
        break;
      }
    }

    if (!groupedCardsNode || groupedCardsPos === null) {
      // Not inside a grouped_cards node
      return false;
    }

    if (dispatch) {
      // Extract child blockquotes
      const children: ProseNode[] = [];
      groupedCardsNode.content.forEach((child) => {
        children.push(child);
      });

      // Replace grouped_cards node with individual blockquotes
      tr.replaceWith(groupedCardsPos, groupedCardsPos + groupedCardsNode.nodeSize, children);

      dispatch(tr);
    }

    return true;
  };
});

/**
 * Keyboard shortcut bindings for grouped-cards commands.
 *
 * - Cmd/Ctrl + Shift + G: Insert grouped cards
 * - Cmd/Ctrl + Shift + U: Ungroup cards (when inside grouped-cards)
 */
export const groupedCardsKeymap = $useKeymap('groupedCardsKeymap', {
  // Cmd/Ctrl + Shift + G: Insert grouped cards
  'Mod-Shift-g': {
    shortcuts: 'Mod-Shift-g',
    command: (ctx) => createCommandRunner(ctx, insertGroupedCardsCommand.key),
  },

  // Cmd/Ctrl + Shift + U: Ungroup cards (when inside grouped-cards)
  'Mod-Shift-u': {
    shortcuts: 'Mod-Shift-u',
    command: (ctx) => createCommandRunner(ctx, ungroupCardsCommand.key),
  },
});

const createCommandRunner = (ctx: Ctx, key: CmdKey<unknown>) => {
  return () => ctx.get(commandsCtx).call(key);
};
